/**
 * Поиск по названию, понимающий русское написание.
 *
 * «БМВ» и «BMW» — одна и та же марка, но ни одного общего знака у них
 * нет. Пока сверка шла подстрокой, человеку приходилось знать, как
 * марка пишется на латинице, — а он её знает по-русски.
 *
 * ## Почему скелет из согласных, а не побуквенный перевод
 *
 * Побуквенный перевод даёт «БМВ» → «bmv» и не совпадает с «bmw»: «в»
 * бывает и `v`, и `w`. Такие развилки есть почти в каждой марке —
 * «Фольксваген» против `Volkswagen` начинается с `ф` против `v`,
 * «Мерседес» против `Mercedes` расходится на `с` против `c`.
 *
 * Перебирать развилки значит плодить десятки написаний на каждый
 * запрос. Вместо этого обе стороны сводятся к одному грубому виду:
 * гласные отбрасываются вовсе, а близкие по звучанию согласные
 * сливаются в одну. «Мерседес» и `Mercedes` дают `mrsds`, «Фольксваген»
 * и `Volkswagen` — `vlsvgn`.
 *
 * ## Грубость здесь — достоинство
 *
 * Сведение теряет различия, и короткие названия могут совпасть с
 * лишними: «Киа» сводится к `s`. Это не беда, а верный размен. Человек
 * выбирает из показанного списка, и лишняя строка стоит ему одного
 * взгляда. Ненайденная марка стоит несостоявшейся подписки.
 *
 * Поэтому сведение — **вторая** попытка. Первой остаётся обычная сверка
 * подстрокой: кто пишет `BMW` латиницей, получает ровно то же, что и
 * прежде.
 */

/** Побуквенный перевод кириллицы. Многобуквенные идут первыми. */
const CYRILLIC: ReadonlyArray<readonly [string, string]> = [
  ['щ', 'sch'],
  ['ш', 'sh'],
  ['ч', 'ch'],
  ['ж', 'zh'],
  ['ю', 'yu'],
  ['я', 'ya'],
  ['ё', 'e'],
  ['а', 'a'],
  ['б', 'b'],
  ['в', 'v'],
  ['г', 'g'],
  ['д', 'd'],
  ['е', 'e'],
  ['з', 'z'],
  ['и', 'i'],
  ['й', 'i'],
  ['к', 'k'],
  ['л', 'l'],
  ['м', 'm'],
  ['н', 'n'],
  ['о', 'o'],
  ['п', 'p'],
  ['р', 'r'],
  ['с', 's'],
  ['т', 't'],
  ['у', 'u'],
  ['ф', 'f'],
  ['х', 'h'],
  ['ц', 'c'],
  ['ы', 'y'],
  ['э', 'e'],
  ['ь', ''],
  ['ъ', ''],
];

/**
 * Слияние близких по звучанию согласных.
 *
 * Каждая строка — один звук, записываемый по-разному в двух языках.
 * `v w f` вместе, потому что «Фольксваген» начинается с `ф`, а
 * `Volkswagen` — с `v`. `c k q s z` вместе, потому что `c` в `Mercedes`
 * звучит как `с`, а в `Cadillac` — как `к`, и различить их правилом не
 * выйдет.
 */
const SOUNDS: ReadonlyArray<readonly [RegExp, string]> = [
  [/[vwf]/gu, 'v'],
  [/[ckqsz]/gu, 's'],
  [/[dt]/gu, 't'],
  [/[bp]/gu, 'b'],
];

/**
 * Гласные, полугласные и `h`: отбрасываются целиком.
 *
 * `h` — вместе с гласными, и это не оплошность. В русском написании она
 * появляется из двузнаков: «ш» даёт `sh`, «ч» — `ch`, «ж» — `zh». В
 * латинском её на том же месте нет: «Шкода» против `Škoda`, «Шевроле»
 * против `Chevrolet`. Оставь мы её — скелеты разошлись бы у каждой
 * марки с шипящей.
 *
 * Самостоятельного звука она тут почти не несёт: «Хендай» против
 * `Hyundai` совпадают и без неё.
 */
const VOWELS = /[aeiouyjh]/gu;

/**
 * Грубый ключ звучания. Пусто — от названия ничего не осталось.
 */
export function soundKey(value: string): string {
  // Диакритика снимается прежде всего: `Škoda` иначе теряет `Š`
  // целиком вместе с первым звуком, и от марки остаётся «koda»
  let text = value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();

  for (const [letter, latin] of CYRILLIC) {
    text = text.split(letter).join(latin);
  }

  text = text.replace(/[^a-z]/gu, '');
  text = text.replace(VOWELS, '');

  for (const [pattern, sound] of SOUNDS) {
    text = text.replace(pattern, sound);
  }

  // Удвоение — вопрос написания, а не звучания: `Hyundai` против
  // «Хёндэ», `Jeep` против «Джип»
  return text.replace(/(.)\1+/gu, '$1');
}

/**
 * Написания, которых не берёт никакое звуковое правило.
 *
 * Французская орфография расходится с произношением так далеко, что
 * скелет из согласных у `Peugeot` (`bgt`) и «Пежо» (`bsg`) не имеет
 * ничего общего: `geot` читается как «жо». Правилом это не выразить —
 * пришлось бы описывать французское чтение целиком ради трёх марок.
 *
 * Поэтому здесь перечень, а не правило. Он заведомо неполон, и это
 * честнее попытки притвориться, будто сведение справляется со всем:
 * добавить строку сюда — минута, а неверное правило испортило бы
 * поиск остальным.
 */
const ALIASES: ReadonlyArray<readonly [string, readonly string[]]> = [
  ['peugeot', ['пежо']],
  ['citroen', ['ситроен', 'ситроён']],
  ['renault', ['рено']],
  ['porsche', ['порше']],
  ['hyundai', ['хендай', 'хёндэ', 'хендэ', 'хундай']],
  ['chevrolet', ['шевроле']],
  ['volkswagen', ['фольксваген', 'вольксваген']],
  ['mercedes', ['мерседес', 'мерс']],
  ['jaguar', ['ягуар']],
  ['chery', ['чери', 'черри']],
  ['geely', ['джили', 'жили']],
  ['haval', ['хавал', 'хавейл']],
  ['exeed', ['эксид']],
  ['changan', ['чанган']],
];

/**
 * Сколько знаков должно быть в запросе, чтобы включалось сведение.
 *
 * На одном знаке скелет совпадает почти с чем угодно, и список
 * перестал бы сужаться вовсе — то есть подсказка превратилась бы в
 * помеху.
 */
const MIN_SOUND_QUERY = 2;

/** Подходит ли название под запрос. */
export function matches(name: string, query: string): boolean {
  const needle = query.trim().toLowerCase();

  if (needle === '') return true;

  // Первой — обычная сверка: кто пишет латиницей, получает ровно то же,
  // что и прежде, без неожиданностей от сведения
  if (name.toLowerCase().includes(needle)) return true;

  if (needle.length < MIN_SOUND_QUERY) return false;

  const lower = name.toLowerCase();

  for (const [latin, russian] of ALIASES) {
    if (lower.includes(latin) && russian.some((one) => one.startsWith(needle))) return true;
  }

  const key = soundKey(needle);

  return key !== '' && soundKey(name).includes(key);
}
